/**
 * SA Tools - Customer Info Section
 * Manage customer information and context
 */
import React from 'react';
import { Paper, Title, Text, TextInput, NumberInput, Stack, Grid, Button, Group } from '@mantine/core';
import { IconDownload, IconDeviceFloppy } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

function CustomerInfoSection({ context, setContext, customerId, setCustomerId, processes }) {
  const handleChange = (field, value) => {
    setContext(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    // Derive customer ID from name if not yet set
    const effectiveId = customerId || context.customer?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || null;

    if (effectiveId) {
      if (!customerId && setCustomerId) setCustomerId(effectiveId);
      sessionStorage.setItem('satools-customerId', effectiveId);
      sessionStorage.setItem(`satools-context-${effectiveId}`, JSON.stringify(context));
      sessionStorage.setItem(`satools-processes-${effectiveId}`, JSON.stringify(processes));

      notifications.show({
        title: 'Success',
        message: 'Customer context saved successfully!',
        color: 'green'
      });
    } else {
      notifications.show({
        title: 'Warning',
        message: 'Please enter a customer name to save',
        color: 'yellow'
      });
    }
  };

  const handleDownloadJSON = () => {
    // Generate customer ID from context if not provided
    const exportCustomerId = customerId || context.customer?.toLowerCase().replace(/\s+/g, '-') || 'customer-data';

    // Create the JSON structure matching the upload format
    const exportData = {
      customerId: exportCustomerId,
      context: context,
      processes: processes || []
    };

    // Convert to JSON string with formatting
    const jsonString = JSON.stringify(exportData, null, 2);

    // Create blob and download
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${exportCustomerId}-context.json`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Paper shadow="sm" p="xl" radius="md">
      <Stack gap="lg">
        <Stack gap={5}>
          <Title order={2} c="purple">Customer Context</Title>
          <Text c="dimmed">Information obtained about the customer during the IPA workshop to help generate the report and ROIs.</Text>
        </Stack>

        <Grid>
          <Grid.Col span={6}>
            <TextInput
              label="Customer Name"
              placeholder="Enter customer name"
              value={context.customer || ''}
              onChange={(e) => handleChange('customer', e.target.value)}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label="Vertical"
              placeholder="e.g., Healthcare, Finance"
              value={context.vertical || ''}
              onChange={(e) => handleChange('vertical', e.target.value)}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label="Line of Business"
              placeholder="e.g., Customer Service"
              value={context.lob || ''}
              onChange={(e) => handleChange('lob', e.target.value)}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <TextInput
              label="IPA Date"
              placeholder="YYYY-MM-DD"
              value={context.ipa_date || ''}
              onChange={(e) => handleChange('ipa_date', e.target.value)}
            />
          </Grid.Col>
        </Grid>

        <Title order={4}>Cost Information</Title>
        <Grid>
          <Grid.Col span={6}>
            <NumberInput
              label="Cost per Agent ($/hour)"
              placeholder="25"
              min={0}
              value={context.cost_per_agent || 25}
              onChange={(value) => handleChange('cost_per_agent', value)}
              thousandSeparator=","
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <NumberInput
              label="Professional Services Price ($)"
              placeholder="0"
              min={0}
              value={context.professional_services_price || 0}
              onChange={(value) => handleChange('professional_services_price', value)}
              thousandSeparator=","
            />
          </Grid.Col>
          <Grid.Col span={4}>
            <NumberInput
              label="BOM Price ($)"
              placeholder="0"
              min={0}
              value={context.bom_price || 0}
              onChange={(value) => handleChange('bom_price', value)}
              thousandSeparator=","
            />
          </Grid.Col>
          <Grid.Col span={4}>
            <NumberInput
              label="BOM Length (months)"
              placeholder="0"
              min={0}
              value={context.bom_length_months || 0}
              onChange={(value) => handleChange('bom_length_months', value)}
            />
          </Grid.Col>
          <Grid.Col span={4}>
            <NumberInput
              label="Monthly Usage Costs ($)"
              placeholder="0"
              min={0}
              value={context.monthly_usage_costs || 0}
              onChange={(value) => handleChange('monthly_usage_costs', value)}
              thousandSeparator=","
            />
          </Grid.Col>
        </Grid>

        <Stack gap="xs">
          <Group>
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              color="purple"
              onClick={handleSave}
              disabled={!context.customer}
            >
              Save Changes
            </Button>
            <Button
              leftSection={<IconDownload size={16} />}
              color="purple"
              variant="light"
              onClick={handleDownloadJSON}
            >
              Download as JSON
            </Button>
          </Group>
          <Text size="xs" c="dimmed">
            Changes are also auto-saved as you type
          </Text>
        </Stack>
      </Stack>
    </Paper>
  );
}

export default CustomerInfoSection;
